import React, { useState } from "react";

import { Grid, MenuItem } from "@mui/material";
import { Option } from "Components/AppGrid/shipeezi-new-grid";
import _ from "lodash";
import { useTranslation } from "react-i18next";

import { ShipmentContainerInfo } from "../../../../../../../../models/shipments";
import { ShipmentContainerInfoDetails, ShipmentDetails } from "../../../../../../../../util/enums";
import { DecimalFormatType } from "../../../../../../../../util/formatDecimalValue";
import { getMeasurement, getWeightMeasurement } from "../../../../../../../../util/getMeasurement";
import { ShipmentObjects } from "../../../../../../../Booking/util";
import { Input, Select } from "../../../../../../../Form";
import Checkbox from "../../../../../../../Form/Checkbox";
import InputMeasurement from "../../../../../../../Form/InputMeasurement";
import getShipmentOptions from "../../../../../../hooks/getShipmentOptions";
import { useShipmentContext } from "../../../../../../hooks/useShipmentContext";
import { DeleteButton, MinusIcon } from "./styles";
import { SelectChangeEvent } from "@mui/material";

interface Props {
  containerInfo: ShipmentContainerInfo;
  index: number;
  viewOnly?: boolean;
  handleDelete?: (container?: string) => void;
}

const ContainerDetails = (props: Props) => {
  const { containerInfo: containerInfoProp, handleDelete, index } = props;

  const { t: translate } = useTranslation();

  const { shipment, handleUpdateShipment, handleChangeOptions } = useShipmentContext();

  const { containerTypeOptions } = getShipmentOptions();

  const measurement = getMeasurement();
  const measurementWeight = getWeightMeasurement();

  const [containerInfo, setContainerInfo] = useState<ShipmentContainerInfo>(containerInfoProp);

  const updateShipmentContainer = (attribute: keyof ShipmentContainerInfo, value: any) => {
    const containers = _.cloneDeep(shipment?.containers || [{}]);

    const containerIndex = containers.findIndex(
      (container) => container.uuid === containerInfoProp.uuid
    );

    containers[containerIndex][attribute] = value;

    handleUpdateShipment("all", {
      [ShipmentDetails.CONTAINERS]: containers,
      [ShipmentDetails.CONTAINER_NUMBERS]: containers
        .map((item) => item?.container)
        .filter((value) => value !== "")
        .join("-"),
    });
    handleChangeOptions(ShipmentObjects.CONTAINERS);
  };
  const onDeleteContainer = () => {
    if (handleDelete) handleDelete(containerInfo?.uuid);
  };

  return (
    <Grid item container direction="row" spacing={2}>
      <Grid item xs={12} sm={6} md={3} lg={3}>
        <Input
          label={`${translate("container")} #`}
          placeholder=""
          required
          value={containerInfo?.[ShipmentContainerInfoDetails.CONTAINER]}
          onChange={(event) =>
            setContainerInfo((prev) => ({
              ...prev,
              [ShipmentContainerInfoDetails.CONTAINER]: event.target.value,
            }))
          }
          onBlur={(event) =>
            updateShipmentContainer(ShipmentContainerInfoDetails.CONTAINER, event.target.value)
          }
          disabled={!!shipment?.shipmentTrackingId}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3} lg={3}>
        <Select
          defaultValue={""}
          displayEmpty
          label={translate("containerSize")}
          placeholder={translate("select")}
          value={containerInfo?.[ShipmentContainerInfoDetails.SIZE]}
          disabled={!shipment?.[ShipmentDetails.LOAD_TYPE]}
          onChange={(event: SelectChangeEvent<any>) =>
            setContainerInfo((prev) => ({
              ...prev,
              [ShipmentContainerInfoDetails.SIZE]: event.target.value,
            }))
          }
          onBlur={(event) =>
            updateShipmentContainer(ShipmentContainerInfoDetails.SIZE, event.target.value)
          }
        >
          {containerTypeOptions.map((option: Option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </Grid>

      <Grid item xs={12} sm={6} md={3} lg={3}>
        <Input
          label={`${translate("seal")} #`}
          placeholder=""
          value={containerInfo?.[ShipmentContainerInfoDetails.SEAL]}
          onChange={(event) =>
            setContainerInfo((prev) => ({
              ...prev,
              [ShipmentContainerInfoDetails.SEAL]: event.target.value,
            }))
          }
          onBlur={(event) =>
            updateShipmentContainer(ShipmentContainerInfoDetails.SEAL, event.target.value)
          }
        />
      </Grid>

      <Grid item lg={3}>
        <DeleteButton display={"flex"} alignItems={"center"}>
          {!!index && !shipment?.shipmentTrackingId && (
            <MinusIcon size={24} onClick={onDeleteContainer} />
          )}
        </DeleteButton>
      </Grid>

      <Grid item xs={12} sm={6} md={3} lg={3}>
        <InputMeasurement
          label={translate("tareWeight")}
          min="0"
          value={containerInfo?.[ShipmentContainerInfoDetails.TARE_WEIGHT] || 0}
          change={(value) => {
            updateShipmentContainer(ShipmentContainerInfoDetails.TARE_WEIGHT, value);
          }}
          measurement={measurementWeight}
          showMeasurement={false}
          decimalPlacesCustom={DecimalFormatType.MAERSK}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3} lg={3}>
        <InputMeasurement
          label={translate("netWeight")}
          min="0"
          value={containerInfo?.[ShipmentContainerInfoDetails.NET_WEIGHT] || 0}
          change={(value) => {
            updateShipmentContainer(ShipmentContainerInfoDetails.NET_WEIGHT, value);
          }}
          measurement={measurementWeight}
          showMeasurement={false}
          decimalPlacesCustom={DecimalFormatType.MAERSK}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3} lg={3}>
        <InputMeasurement
          label={translate("grossWeight")}
          min="0"
          value={containerInfo?.[ShipmentContainerInfoDetails.GROSS_WEIGHT] || 0}
          change={(value) => {
            updateShipmentContainer(ShipmentContainerInfoDetails.GROSS_WEIGHT, value);
          }}
          measurement={measurementWeight}
          showMeasurement={false}
          decimalPlacesCustom={DecimalFormatType.MAERSK}
        />
      </Grid>
      <Grid item xs={12} container alignItems="flex-start">
        <Grid item md={3} lg={3}></Grid>
        <Grid item md={3} lg={3}></Grid>
        <Grid item xs={12} sm={6} md={3} lg={3}>
          <Checkbox
            className="ml-2"
            label={translate("verifiedGrossWeight")}
            checked={containerInfo?.[ShipmentContainerInfoDetails.VERIFIED_GROSS_WEIGHT]}
            onChange={(_, checked) =>
              setContainerInfo((prev) => ({
                ...prev,
                [ShipmentContainerInfoDetails.VERIFIED_GROSS_WEIGHT]: checked,
              }))
            }
            onBlur={() =>
              updateShipmentContainer(
                ShipmentContainerInfoDetails.VERIFIED_GROSS_WEIGHT,
                containerInfo?.[ShipmentContainerInfoDetails.VERIFIED_GROSS_WEIGHT]
              )
            }
          />
        </Grid>
      </Grid>
      <Grid item xs={12} sm={6} md={3} lg={3}>
        <InputMeasurement
          label={translate("grossVolume")}
          min="0"
          value={containerInfo?.[ShipmentContainerInfoDetails.GROSS_VOLUME] || 0}
          change={(value) => {
            updateShipmentContainer(ShipmentContainerInfoDetails.GROSS_VOLUME, value);
          }}
          measurement={measurement}
          showMeasurement={false}
          decimalPlacesCustom={DecimalFormatType.MAERSK}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3} lg={3}>
        <InputMeasurement
          label={translate("volumetricWeight")}
          min="0"
          value={containerInfo?.[ShipmentContainerInfoDetails.VOLUMETRIC_WEIGHT] || 0}
          change={(value) => {
            updateShipmentContainer(ShipmentContainerInfoDetails.VOLUMETRIC_WEIGHT, value);
          }}
          measurement={measurementWeight}
          showMeasurement={false}
          decimalPlacesCustom={DecimalFormatType.MAERSK}
        />
      </Grid>
    </Grid>
  );
};

export default ContainerDetails;
